/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 */

"use client";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useUtmParams } from "@/hooks/marketing/use-utm-params";
import { ArrowRight, CheckCircle, Loader2 } from "lucide-react";
import { useState, useCallback } from "react";

// ============================================================================
// TYPES
// ============================================================================

interface EmailCaptureFormProps {
  source?: "email_capture" | "demo_request";
  heading?: string;
  description?: string;
  buttonText?: string;
  successMessage?: string;
  variant?: "inline" | "stacked";
  showExtraFields?: boolean;
  className?: string;
  inputClassName?: string;
  buttonClassName?: string;
  onSuccess?: () => void;
}

type FormState = "idle" | "loading" | "success" | "error";

const ROLE_OPTIONS = [
  { value: "contractor", label: "Contractor / Tradesperson" },
  { value: "estimator", label: "Estimator" },
  { value: "project_manager", label: "Project Manager" },
  { value: "business_owner", label: "Business Owner" },
  { value: "other", label: "Other" },
] as const;

const COMPANY_SIZE_OPTIONS = [
  { value: "1", label: "Just me" },
  { value: "2-5", label: "2-5 employees" },
  { value: "6-10", label: "6-10 employees" },
  { value: "11-25", label: "11-25 employees" },
  { value: "26-50", label: "26-50 employees" },
  { value: "51+", label: "51+ employees" },
] as const;

// ============================================================================
// COMPONENT
// ============================================================================

export function EmailCaptureForm({
  source = "email_capture",
  heading,
  description,
  buttonText = "Start Your Free Trial",
  successMessage = "Thanks! We'll be in touch soon.",
  variant = "inline",
  showExtraFields = false,
  className = "",
  inputClassName = "",
  buttonClassName = "",
  onSuccess,
}: EmailCaptureFormProps) {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [company, setCompany] = useState("");
  const [phone, setPhone] = useState("");
  const [role, setRole] = useState("");
  const [companySize, setCompanySize] = useState("");
  const [formState, setFormState] = useState<FormState>("idle");
  const [errorMessage, setErrorMessage] = useState("");
  const utmParams = useUtmParams();

  const handleSubmit = useCallback(
    async (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();

      if (!email || !email.includes("@")) {
        setErrorMessage("Please enter a valid email address");
        setFormState("error");
        return;
      }

      setFormState("loading");
      setErrorMessage("");

      try {
        const response = await fetch("/api/marketing/leads", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            email,
            ...(name && { name }),
            ...(company && { company }),
            ...(phone && { phone }),
            ...(role && { role }),
            ...(companySize && { company_size: companySize }),
            source,
            ...utmParams,
          }),
        });

        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.error?.message || "Something went wrong");
        }

        setFormState("success");
        setEmail("");
        setName("");
        setCompany("");
        setPhone("");
        setRole("");
        setCompanySize("");
        onSuccess?.();
      } catch (error) {
        setErrorMessage(
          error instanceof Error ? error.message : "Something went wrong",
        );
        setFormState("error");
      }
    },
    [
      email,
      name,
      company,
      phone,
      role,
      companySize,
      source,
      utmParams,
      onSuccess,
    ],
  );

  // Success state
  if (formState === "success") {
    return (
      <div className={`flex items-center gap-3 animate-scale-in ${className}`}>
        <CheckCircle className="w-5 h-5 text-secondary flex-shrink-0" />
        <span className="text-sm text-muted-foreground">{successMessage}</span>
      </div>
    );
  }

  const isInline = variant === "inline";

  return (
    <div className={className}>
      {heading && <h3 className="text-lg font-semibold mb-2">{heading}</h3>}
      {description && (
        <p className="text-sm text-muted-foreground mb-4">{description}</p>
      )}

      <form
        onSubmit={handleSubmit}
        className={
          isInline && !showExtraFields
            ? "flex flex-col sm:flex-row gap-3"
            : "flex flex-col gap-3"
        }
      >
        <Input
          type="email"
          placeholder="Email address *"
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
            if (formState === "error") {
              setFormState("idle");
              setErrorMessage("");
            }
          }}
          disabled={formState === "loading"}
          className={`${isInline && !showExtraFields ? "flex-1" : "w-full"} bg-background ${inputClassName}`}
          aria-label="Email address"
          aria-invalid={formState === "error"}
        />

        {showExtraFields && (
          <>
            <Input
              type="text"
              placeholder="Your name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={formState === "loading"}
              className={`w-full bg-background ${inputClassName}`}
              aria-label="Name"
            />

            <div className="grid grid-cols-2 gap-3">
              <Select
                value={role}
                onValueChange={setRole}
                disabled={formState === "loading"}
              >
                <SelectTrigger className="bg-background" aria-label="Role">
                  <SelectValue placeholder="Your role" />
                </SelectTrigger>
                <SelectContent>
                  {ROLE_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select
                value={companySize}
                onValueChange={setCompanySize}
                disabled={formState === "loading"}
              >
                <SelectTrigger
                  className="bg-background"
                  aria-label="Company size"
                >
                  <SelectValue placeholder="Company size" />
                </SelectTrigger>
                <SelectContent>
                  {COMPANY_SIZE_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <Input
              type="text"
              placeholder="Company name"
              value={company}
              onChange={(e) => setCompany(e.target.value)}
              disabled={formState === "loading"}
              className={`w-full bg-background ${inputClassName}`}
              aria-label="Company"
            />
          </>
        )}

        <Button
          type="submit"
          disabled={formState === "loading" || !email}
          className={`bg-secondary hover:bg-secondary/90 text-white font-medium ${buttonClassName}`}
        >
          {formState === "loading" ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Submitting...
            </>
          ) : (
            <>
              {buttonText}
              <ArrowRight className="w-4 h-4 ml-2" />
            </>
          )}
        </Button>
      </form>

      {formState === "error" && errorMessage && (
        <p className="text-sm text-destructive mt-2" role="alert">
          {errorMessage}
        </p>
      )}
    </div>
  );
}
